import { Link } from "react-router-dom";
import { UserProgram } from "../../types/user.response";
import { ProgramSummaryResponse } from "../../types/program.response";
import ProgramProgressSkeleton from "../../components/loading/ProgramProgressSkeleton";

export type ProgramLoadState = {
    status: 'loading' | 'loaded' | 'error';
    data?: ProgramSummaryResponse;
    error?: string;
};

interface HomeSidebarLoggedInProps {
    userPrograms?: UserProgram[];
    programStates?: Map<string, ProgramLoadState>;
}

export default function HomeSidebarLoggedIn({ userPrograms, programStates }: HomeSidebarLoggedInProps) {
    // 所有 program 都有结果(loaded/error)后再一起展示,避免逐个跳出
    const allSettled = (userPrograms ?? []).every(p => {
        const state = programStates?.get(p.programId);
        return state !== undefined && state.status !== 'loading';
    });

    return (
        <div className="bg-gray-50 rounded-md p-3">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">My Programs</h2>
            <div className="flex flex-col gap-4">
                {[...(userPrograms ?? [])].sort((a, b) => {
                    const order = { college: 0, major: 1, minor: 2 };
                    return order[a.type] - order[b.type];
                }).map((userProgram) => {
                    const state = programStates?.get(userProgram.programId);
                    const isLoading = !allSettled;
                    const program = state?.data;

                    const completedCount = program?.summary.completedCoursesCount ?? 0;
                    const requiredCount = program?.summary.requiredCoursesCount ?? 0;
                    const progressPct = requiredCount > 0
                        ? Math.min(100, Math.round((completedCount / requiredCount) * 100))
                        : 0;

                    return (
                        <Link
                            key={userProgram.programId}
                            to={`/programs/${userProgram.programId}?view=checklist`}
                            className="block rounded-md px-2 py-2 -mx-2 hover:bg-white hover:shadow-sm border border-transparent hover:border-gray-200 transition-all duration-150"
                        >
                            <span className="text-sm font-medium text-gray-800 group-hover:text-blue-600">
                                {userProgram.name}
                            </span>
                            {isLoading ? (
                                <ProgramProgressSkeleton />
                            ) : (
                                <div className="mt-1.5">
                                    <div className="flex items-baseline justify-between mb-1">
                                        <span className="text-xs text-gray-500">
                                            {completedCount} / {requiredCount} courses
                                        </span>
                                        <span className="text-xs text-gray-400">{progressPct}%</span>
                                    </div>
                                    <div className="h-1.5 overflow-hidden rounded-full bg-gray-200">
                                        <div
                                            className="h-full rounded-full bg-blue-500 transition-[width] duration-300 ease-out"
                                            style={{ width: `${progressPct}%` }}
                                        />
                                    </div>
                                </div>
                            )}
                        </Link>
                    );
                })}
            </div>
            <p className="mt-4 pt-3 border-t border-gray-200 text-xs text-gray-500 leading-snug">
                For planning only — this does not guarantee that you've met all requirements. Always verify with official sources.
            </p>
            <p className="mt-1 pt-2 text-xs text-gray-500 leading-snug">
                Found an issue?{' '}
                <a href="https://forms.gle/QjjAxdeXQms7rNCF8" target="_blank" rel="noopener noreferrer" className="underline text-blue-400 hover:text-blue-600">Report here</a>
            </p>
        </div>
    );
}
